import React, { useState, useEffect } from 'react';
import { db } from '../instant';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DEFAULT_ROUTE } from '../constants';
import { userHasAppAccess } from '../lib/userAppAccess';

function Login() {
    const [sentEmail, setSentEmail] = useState('');
    return (
        <div style={authStyles.container}>{!sentEmail ? <Email setSentEmail={setSentEmail} /> : <MagicCode sentEmail={sentEmail} />}</div>
    );
}

interface EmailProps {
    setSentEmail: (email: string) => void;
}

function Email({ setSentEmail }: EmailProps) {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');

        if (!email) {
            return;
        }

        try {
            // Send magic code without checking permissions first
            await db.auth.sendMagicCode({ email });
            setSentEmail(email);
        } catch (err: any) {
            setError('Error: ' + (err?.body?.message || err?.message || String(err)));
        }
    };

    return (
        <form onSubmit={handleSubmit} style={authStyles.form}>
            <h2 style={{ color: '#333', marginBottom: '20px', textAlign: 'center', fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>
                Let&apos;s log you in!
            </h2>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                    style={authStyles.input}
                    placeholder="Enter your email"
                    type="email"
                    value={email}
                    onChange={e => {
                        setEmail(e.target.value);
                        setError('');
                    }}
                />
                {error && (
                    <div
                        style={{
                            color: '#d32f2f',
                            fontSize: '14px',
                            marginTop: '-10px',
                            marginBottom: '10px',
                            textAlign: 'center',
                            maxWidth: '300px',
                        }}
                    >
                        {error}
                    </div>
                )}
            </div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <button type="submit" style={authStyles.button}>
                    Send Code
                </button>
            </div>
        </form>
    );
}

interface MagicCodeProps {
    sentEmail: string;
}

function MagicCode({ sentEmail }: MagicCodeProps) {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');
        db.auth.signInWithMagicCode({ email: sentEmail, code }).catch(err => {
            setError('Error: ' + (err?.body?.message || err?.message || String(err)));
            setCode('');
        });
    };

    return (
        <form onSubmit={handleSubmit} style={authStyles.form}>
            <h2
                style={{
                    color: '#333',
                    marginBottom: '20px',
                    textAlign: 'center',
                    fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
                    lineHeight: '1.4',
                }}
            >
                Okay, we sent you an email! What was the code?
            </h2>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <input
                    style={authStyles.input}
                    type="text"
                    placeholder="123456..."
                    value={code}
                    onChange={e => {
                        setCode(e.target.value);
                        setError('');
                    }}
                />
                {error && (
                    <div
                        style={{
                            color: '#d32f2f',
                            fontSize: '14px',
                            marginTop: '-10px',
                            marginBottom: '10px',
                            textAlign: 'center',
                            maxWidth: '300px',
                        }}
                    >
                        {error}
                    </div>
                )}
            </div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <button type="submit" style={authStyles.button}>
                    Verify
                </button>
            </div>
        </form>
    );
}

const authStyles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: '20px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif',
        width: '100%',
        maxWidth: '400px',
    },
    input: {
        padding: '12px',
        marginBottom: '15px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        width: '100%',
        maxWidth: '300px',
        fontSize: '16px',
        boxSizing: 'border-box',
    },
    button: {
        padding: '12px 24px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '16px',
        width: '100%',
        maxWidth: '300px',
    },
};

export default function LoginScreen() {
    const { isLoading, user, error } = db.useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Query user data once authenticated
    const { data: userData, isLoading: userDataLoading } = db.useQuery(
        user
            ? {
                  $users: {
                      $: {
                          where: {
                              id: user.id,
                          },
                      },
                  },
              }
            : null
    );

    // Compute derived state
    const currentUser = (userData as any)?.$users?.[0];
    const hasAppAccess = userHasAppAccess(currentUser);

    const isLoadingPermissions = isLoading || (user && userDataLoading);
    const showPermissionError = !!currentUser && !hasAppAccess && !isLoadingPermissions;

    // Handle side effects: navigation and sign out
    useEffect(() => {
        if (!isLoading && !error && user && !userDataLoading) {
            if (!hasAppAccess) {
                // No capability flags and no invite-scoped events
                db.auth.signOut();
            } else {
                const nextRoute = searchParams.get('redirect') || DEFAULT_ROUTE;
                navigate(nextRoute, { replace: true });
            }
        }
    }, [isLoading, error, user, hasAppAccess, userDataLoading, navigate, searchParams]);

    if (isLoadingPermissions) {
        return <div>Loading...</div>;
    }
    if (error) {
        return <div>Uh oh! {error.message}</div>;
    }
    if (showPermissionError) {
        return (
            <div style={authStyles.container}>
                <div style={authStyles.form}>
                    <div style={{ color: '#d32f2f', padding: '10px', textAlign: 'center' }}>
                        You don&apos;t have access to this application. Please use an invite link or contact your administrator.
                    </div>
                </div>
            </div>
        );
    }
    if (user) {
        // Return null while redirect is pending
        return null;
    }
    return <Login />;
}
